import { useCallback, useEffect, useRef } from "react";
import * as faceapi from "@vladmandic/face-api";

const DETECTION_INTERVAL_MS = 1000;
const REQUIRED_CONSECUTIVE_HITS = 1;
const MODELS_URI = "/models";

type UseChildrenModeAgeDetectionParams = {
  enabled: boolean;
  ageThreshold: number;
  isChildrenMode: boolean;
  setIsChildrenMode: (value: boolean) => void;
  isPreviewMode: boolean;
};

export const useChildrenModeAgeDetection = ({
  enabled,
  ageThreshold,
  isChildrenMode,
  setIsChildrenMode,
  isPreviewMode,
}: UseChildrenModeAgeDetectionParams) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const modelsLoadedRef = useRef(false);
  const consecutiveHitsRef = useRef(0);

  const stopDetection = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled || isPreviewMode || isChildrenMode) {
      stopDetection();
      return;
    }

    let cancelled = false;

    const start = async () => {
      try {
        if (!modelsLoadedRef.current) {
          await Promise.all([
            faceapi.nets.tinyFaceDetector.loadFromUri(MODELS_URI),
            faceapi.nets.ageGenderNet.loadFromUri(MODELS_URI),
          ]);
          modelsLoadedRef.current = true;
        }

        if (cancelled) return;

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        const video = videoRef.current;
        if (!video) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        video.srcObject = stream;
        await video.play();

        const detect = async () => {
          if (
            cancelled ||
            !videoRef.current ||
            videoRef.current.readyState < 2
          ) {
            return;
          }

          try {
            const detection = await faceapi
              .detectSingleFace(
                videoRef.current,
                new faceapi.TinyFaceDetectorOptions(),
              )
              .withAgeAndGender();

            if (detection && detection.age < ageThreshold) {
              consecutiveHitsRef.current += 1;
              if (consecutiveHitsRef.current >= REQUIRED_CONSECUTIVE_HITS) {
                setIsChildrenMode(true);
                stopDetection();
              }
            } else {
              consecutiveHitsRef.current = 0;
            }
          } catch (error) {
            console.warn("Children mode age detection failed:", error);
          }
        };

        intervalRef.current = setInterval(detect, DETECTION_INTERVAL_MS);
      } catch (error) {
        console.warn(
          "Camera access for children mode age detection failed:",
          error,
        );
      }
    };

    start();

    return () => {
      cancelled = true;
      consecutiveHitsRef.current = 0;
      stopDetection();
    };
  }, [
    enabled,
    ageThreshold,
    isChildrenMode,
    isPreviewMode,
    setIsChildrenMode,
    stopDetection,
  ]);

  return { videoRef };
};
